// Thanks page for the guided course funnel — /build-india/thanks. Razorpay
// redirects here after payment. Fires the Meta Purchase event (value/currency
// from the tier config) exactly once per registration, and reports the
// razorpay_payment_id back via /api/webinar-confirm when the redirect carries
// one (Razorpay Payment Pages often drop it — issue #113 — so payment_status
// is client-reported; reconcile against the Razorpay dashboard).
// TEST MODE: if the tier's paymentLink is empty, no payment happened, so the
// Purchase pixel is NOT fired.
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BUILD_REGIONS, BuildRegionKey, BuildTierKey } from './buildCourseConfig';

const BuildCourseThanksPage: React.FC<{ region: BuildRegionKey }> = ({ region }) => {
  const cfg = BUILD_REGIONS[region];
  const navigate = useNavigate();
  const tierKey = (localStorage.getItem('build_tier') as BuildTierKey) || 'professional';
  const tier = cfg.tiers[tierKey] || cfg.tiers.professional;

  useEffect(() => {
    const regId = localStorage.getItem('build_reg_id');
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('razorpay_payment_id');

    // Report the payment id when Razorpay passes it through on redirect
    if (regId && paymentId) {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      fetch(`${apiUrl}/api/webinar-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_id: regId, razorpay_payment_id: paymentId }),
      }).catch(() => { /* best-effort — reconcile in Razorpay dashboard */ });
    }

    // Purchase events (Meta + Google Ads): once per registration, and only
    // when a real payment link exists (test mode must not pollute Purchase data)
    const fireKey = `build_purchase_fired_${regId || 'anon'}`;
    if (tier.paymentLink && !sessionStorage.getItem(fireKey)) {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Purchase', {
          content_name: `${cfg.slug}_${tier.key}`,
          value: tier.price,
          currency: cfg.currency,
        });
      }
      // Google Ads conversion (cubemate.app account, action PURCHASE_1) —
      // routed via the site's Google tag, which has that account as a destination
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'ads_conversion_PURCHASE_1', {
          value: tier.price,
          currency: cfg.currency,
          transaction_id: paymentId || undefined,
        });
      }
      sessionStorage.setItem(fireKey, '1');
    }
  }, [cfg.slug, cfg.currency, tier.key, tier.price, tier.paymentLink]);

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16 space-x-3">
          <img src="/cubemate-logo.png" alt="Cubemate" className="w-9 h-9 rounded-lg" />
          <span className="text-xl font-bold">Cubemate</span>
        </div>
      </nav>

      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-xl text-center">
          <div className="text-6xl mb-6">🎉</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-4">
            You're in — welcome to <span className="text-violet-600">{tier.name}</span>.
          </h1>
          <p className="text-gray-600 mb-8">
            Your build starts now. Within 24 hours you'll get an email with your
            onboarding steps{cfg.whatsappLink ? ' and the course WhatsApp group' : ''} — keep an eye on your inbox
            (and spam folder, just in case).
          </p>

          <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50 text-left mb-8">
            <p className="font-bold mb-3">While you wait, get a head start:</p>
            <ul className="space-y-2 text-gray-600 text-sm">
              <li>1. Create your Cubemate account — your Ultimate access is activated on it.</li>
              <li>2. Try your first prompt: "Teach me Python basics with runnable examples".</li>
              <li>3. Your Day-1 project brief arrives with the onboarding email.</li>
            </ul>
          </div>

          {cfg.whatsappLink && (
            <a
              href={cfg.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto px-8 py-3 mb-4 rounded-xl border-2 border-green-600 text-green-700 font-bold hover:bg-green-50 transition-all"
            >
              Join the WhatsApp group
            </a>
          )}
          <div>
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 text-lg font-bold rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/25"
            >
              Create your Cubemate account
            </button>
          </div>
        </div>
      </main>

      <footer className="py-6 px-4 border-t border-gray-200 text-center text-sm text-gray-500">
        © {new Date().getFullYear()} Cubemate
      </footer>
    </div>
  );
};

export default BuildCourseThanksPage;
